import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listFiles } from "../api/files";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const TYPE_TO_CAT = {
  pdf: "PDF",
  docx: "Docx",
  epub: "Docx",
  txt: "Docx",
  mp3: "Audio",
  wav: "Audio",
  mp4: "Video",
  youtube: "Video",
  url: "URL",
};

const CATEGORIES = ["PDF", "Docx", "Video", "Audio", "URL"];

function NavItem({ icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-4 px-5 py-2 text-sm text-dark-muted hover:text-dark-text transition-colors"
    >
      <ion-icon name={icon} style={{ fontSize: "20px", flexShrink: 0 }} />
      <span className="font-medium">{label}</span>
    </button>
  );
}

function RailIcon({ icon, label, onClick, badge }) {
  return (
    <button
      onClick={onClick}
      title={label}
      className="relative flex h-10 w-10 items-center justify-center rounded-full text-dark-muted hover:bg-[#2B2B30] hover:text-dark-text transition-colors"
    >
      <ion-icon name={icon} style={{ fontSize: "20px" }} />
      {badge && (
        <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-accent" />
      )}
    </button>
  );
}

function SelectionDot({ selected, disabled }) {
  if (selected) {
    return (
      <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-green-500">
        <ion-icon name="checkmark" style={{ fontSize: "11px", color: "#fff" }} />
      </span>
    );
  }
  return (
    <span
      className={`flex h-4 w-4 flex-shrink-0 rounded-full border ${
        disabled ? "border-[#333]" : "border-[#444]"
      }`}
    />
  );
}

const STATUS_LABEL = {
  pending: { text: "indexing…", className: "text-blue-400" },
  pending_approval: { text: "needs approval", className: "text-orange-400" },
  failed: { text: "failed", className: "text-red-400" },
};

function FileStatusBadge({ status }) {
  const cfg = STATUS_LABEL[status];
  if (!cfg) return null;
  return (
    <span className={`text-[10px] italic ${cfg.className}`}>
      {cfg.text}
    </span>
  );
}

export default function Sidebar({
  selectedChatId,
  onSelectChat,
  onNewChat,
  onOpenAllChats,
  selectedFileIds,
  onSelectionChange,
  collapsed,
  onToggle,
}) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [expandedCats, setExpandedCats] = useState({ PDF: true });

  const { data: filesData } = useQuery({
    queryKey: ["files"],
    queryFn: async () => {
      const res = await listFiles();
      return res.data.data;
    },
    refetchInterval: (q) => {
      const list = q.state.data || [];
      const stillWorking = list.some((f) =>
        ["pending", "pending_approval"].includes(f.indexed_status)
      );
      return stillWorking ? 3000 : false;
    },
  });

  const files = filesData || [];

  // ── Collapsed: icon rail ──────────────────────────────────────────────
  if (collapsed) {
    const initial = (user?.email?.[0] || "U").toUpperCase();
    return (
      <div className="flex h-full w-14 flex-shrink-0 flex-col items-center bg-dark-surface pt-5 pb-3">
        <button
          onClick={onToggle}
          title="Expand sidebar"
          className="flex h-10 w-10 items-center justify-center rounded-full text-dark-muted hover:bg-[#2B2B30] hover:text-dark-text transition-colors"
        >
          <ion-icon name="menu-outline" style={{ fontSize: "26px" }} />
        </button>

        <div className="mt-5 flex flex-col gap-1">
          <RailIcon icon="add-outline" label="New Chat" onClick={onNewChat} />
          <RailIcon icon="chatbubbles-outline" label="All Chats" onClick={onOpenAllChats} />
          <RailIcon
            icon="cloud-upload-outline"
            label="Files & Documents"
            onClick={() => navigate(isAdmin ? "/admin" : "/my-files")}
          />
          {isAdmin && (
            <RailIcon
              icon="settings-outline"
              label="Settings"
              onClick={() => navigate("/settings")}
            />
          )}
        </div>

        <div className="mt-auto flex flex-col items-center gap-3">
          <button
            onClick={logout}
            title="Logout"
            className="flex h-10 w-10 items-center justify-center rounded-full text-dark-muted hover:bg-[#2B2B30] hover:text-dark-text transition-colors"
          >
            <ion-icon name="log-out-outline" style={{ fontSize: "19px" }} />
          </button>
          <button
            onClick={() => navigate("/profile")}
            title={user?.email}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#e5e5e5] text-sm font-semibold text-[#1a1a1a] hover:opacity-90"
          >
            {initial}
          </button>
        </div>
      </div>
    );
  }

  // ── Expanded: full sidebar ────────────────────────────────────────────
  const toggleCat = (cat) =>
    setExpandedCats((prev) => ({ ...prev, [cat]: !prev[cat] }));

  const toggleFile = (id) => {
    onSelectionChange(
      selectedFileIds.includes(id)
        ? selectedFileIds.filter((x) => x !== id)
        : [...selectedFileIds, id]
    );
  };

  const filesByCat = (cat) =>
    files.filter((f) => TYPE_TO_CAT[f.file_type] === cat);

  const displayName = (f) => {
    const n = f.original_name || f.name;
    return n.length > 26 ? n.slice(0, 24) + "…" : n;
  };

  return (
    <div className="flex h-full w-[300px] flex-shrink-0 flex-col bg-dark-surface">
      {/* Top: hamburger + collapse arrow */}
      <div className="flex items-center justify-between px-5 pt-5 pb-4">
        <button
          onClick={onToggle}
          title="Collapse sidebar"
          className="flex h-10 items-center text-dark-muted hover:text-dark-text transition-colors"
        >
          <ion-icon name="menu-outline" style={{ fontSize: "26px" }} />
        </button>
        <button
          onClick={onToggle}
          title="Collapse sidebar"
          className="text-dark-muted hover:text-dark-text transition-colors"
        >
          <ion-icon name="chevron-back-outline" style={{ fontSize: "20px" }} />
        </button>
      </div>

      {/* Primary nav */}
      <nav className="mt-1 space-y-0.5">
        <NavItem icon="add-circle-outline" label="New Chat" onClick={onNewChat} />
        <NavItem icon="chatbubbles-outline" label="All Chats" onClick={onOpenAllChats} />
        <NavItem
          icon="cloud-upload-outline"
          label="Files & Documents"
          onClick={() => navigate(isAdmin ? "/admin" : "/my-files")}
        />
        {isAdmin && (
          <NavItem
            icon="settings-outline"
            label="Settings"
            onClick={() => navigate("/settings")}
          />
        )}
      </nav>

      {/* Files & Documents */}
      <div className="flex-1 overflow-y-auto mt-8">
        <p className="px-5 mb-3 text-xs font-semibold text-dark-muted tracking-wider uppercase">
          Files &amp; Documents
        </p>

        {CATEGORIES.map((cat) => {
          const catFiles = filesByCat(cat);
          const expanded = !!expandedCats[cat];

          return (
            <div key={cat}>
              <button
                onClick={() => toggleCat(cat)}
                className="flex w-full items-center gap-3 px-5 py-2 text-sm text-dark-muted hover:text-dark-text transition-colors"
              >
                <ion-icon
                  name={expanded ? "chevron-down-outline" : "chevron-forward-outline"}
                  style={{ fontSize: "12px", flexShrink: 0 }}
                />
                <span className="font-medium">{cat}</span>
                {catFiles.length > 0 && (
                  <span className="ml-auto w-4 text-center text-[10px] text-dark-muted opacity-60">
                    {catFiles.length}
                  </span>
                )}
              </button>

              {expanded && catFiles.length > 0 && (
                <div className="mb-1">
                  {catFiles.map((f) => {
                    const selected = selectedFileIds.includes(f.id);
                    const indexed = f.indexed_status === "indexed";
                    return (
                      <button
                        key={f.id}
                        onClick={() => indexed && toggleFile(f.id)}
                        title={
                          indexed
                            ? f.original_name || f.name
                            : `${f.original_name || f.name} — ${f.indexed_status}${
                                f.error_message ? `: ${f.error_message}` : ""
                              }`
                        }
                        className={`flex w-full items-center justify-between gap-3 pl-12 pr-5 py-1.5 text-xs transition-colors ${
                          !indexed
                            ? "text-[#666] cursor-default"
                            : selected
                            ? "text-dark-text"
                            : "text-dark-muted hover:text-dark-text cursor-pointer"
                        }`}
                      >
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                          <span className="truncate text-left">{displayName(f)}</span>
                          <FileStatusBadge status={f.indexed_status} />
                        </span>
                        <SelectionDot selected={selected && indexed} disabled={!indexed} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* User info */}
      <div className="flex items-center gap-3 px-5 py-4">
        <button
          onClick={() => navigate("/profile")}
          title="Open profile"
          className="flex flex-1 items-center gap-3 min-w-0 hover:opacity-90 text-left"
        >
          <div className="h-9 w-9 flex-shrink-0 rounded-full bg-[#e5e5e5] flex items-center justify-center">
            <span className="text-sm font-semibold text-[#1a1a1a]">
              {(user?.name?.[0] || user?.email?.[0] || "U").toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-dark-text leading-tight truncate">
              {user?.name || user?.username || user?.email?.split("@")[0] || "User"}
            </p>
            <p className="text-xs text-dark-muted truncate">{user?.email}</p>
          </div>
        </button>
        <button
          onClick={logout}
          title="Logout"
          className="text-dark-muted hover:text-dark-text transition-colors"
        >
          <ion-icon name="log-out-outline" style={{ fontSize: "19px" }} />
        </button>
      </div>
    </div>
  );
}
